<template>
  <section
    v-editable="blok"
    :class="blok.isHScreen ? 'h-screen' : 'h-default'"
    class="article-bg-video theme-white"
  >
    <div class="article-bg-video__video-container">
      <video class="article-bg-video__video" loop muted autoplay>
        <source :src="blok.video.vimeo_raw" type="video/mp4" />
      </video>
    </div>
    <div
      :class="showVideo ? 'article-bg-video__layer--hide' : ''"
      class="article-bg-video__layer"
    ></div>
    <div class="container">
      <div
        :class="showVideo ? 'article-bg-video__content--hide' : ''"
        class="article-bg-video__content row"
      >
        <div class="col-12 article-bg-video__content-inner">
          <div v-editable="blok" class="col-12 article-bg-video__title">
            <h2 ref="title">{{ blok.title }}</h2>
          </div>
          <div class="col-12 col-md-6 article-bg-video__content-text">
            <component
              :is="item.component | dashify"
              v-for="item in blok.contents"
              :ref="'description'"
              :key="item._uid"
              :blok="item"
              modifier-class="article-bg-video__text"
            />
            <component
              :is="'a_link' | dashify"
              ref="link"
              :key="blok._uid"
              :blok="getLinkConfig(blok)"
              modifier-class="icon icon--play article-bg-video__link"
              @click.native.prevent="showVideo = true"
            />
          </div>
          <div
            class="col-12 icon icon--close article-bg-video__close"
            @click="showVideo = false"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { TimelineMax, Power2 } from 'gsap'

export default {
  name: 'ArticleBgVideo',
  props: {
    blok: {
      type: Object,
      default: () => {},
    },
    visible: {
      type: Boolean,
      default: () => false,
    },
    visibleSectionIndex: {
      type: Number,
      default: () => 1000,
    },
  },
  data() {
    return {
      animated: false,
      tl: {},
      showVideo: false,
    }
  },
  watch: {
    visible(visible) {
      if (visible) {
        this.$store.commit(
          'globals/setVisibleSectionName',
          this.blok.breadcrumb
        )
        this.$store.commit(
          'globals/setVisibleSectionIndex',
          this.visibleSectionIndex
        )

        if (!this.animated) {
          const { title, description, link } = this.$refs

          this.tl.fromTo(
            [title].filter((e) => {
              return e
            }),
            0.3,
            { y: 25, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, ease: Power2.easeOut },
            0.7
          )
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < description.length; i++) {
            this.tl.fromTo(
              [description[i].$el].filter((e) => {
                return e
              }),
              0.3,
              { y: 0, autoAlpha: 0 },
              { y: 0, autoAlpha: 1, ease: Power2.easeOut },
              1
            )
          }
          this.tl.fromTo(
            [link.$el].filter((e) => {
              return e
            }),
            0.3,
            { y: 25, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, ease: Power2.easeOut },
            1.3
          )

          this.animated = true
        }
      }
    },
  },
  mounted() {
    this.tl = new TimelineMax({
      delay: 0.5,
    })
  },
  methods: {
    getLinkConfig(blok) {
      return {
        text: blok.videoLinkText,
        link: {
          url: '#',
        },
      }
    },
  },
}
</script>

<style lang="scss">
@import '../../assets/scss/settings';

.article-bg-video {
  display: flex;
  position: relative;
  overflow: hidden;

  &__layer {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 0;
    background-color: $white;
    opacity: 0.9;
    transition: opacity 0.3s ease-out;

    &--hide {
      opacity: 0;
    }
  }

  &__video-container {
    position: absolute;
    min-width: 100%;
    min-height: 100%;

    video {
      position: absolute;
      min-width: 100%;
      min-height: 100%;
      display: block;
      transform: translate(-50%, -50%);
      left: 50%;
      top: 50%;
      z-index: 0;
    }
  }

  &__content {
    position: relative;
    overflow: hidden;
    height: 100%;
    justify-content: center;
    flex-direction: column;

    .col-12 {
      flex: 0 0 auto;
    }

    &--hide {
      .article-bg-video__content-text {
        position: static;
      }

      .article-bg-video__title,
      .article-bg-video__content-text {
        opacity: 0;
      }

      .article-bg-video__close {
        top: calc(50% - 27px);
        opacity: 1;
        z-index: 1;
        transform: rotate(360deg);
      }
    }
  }

  &__content-text {
    padding: 0;
    transition: opacity 0.3s ease-out;
    opacity: 1;
  }

  &__title {
    transition: opacity 0.3s ease-out;
    opacity: 1;
    padding: 0;

    h2 {
      @include font-family($font-family-headings-bold, $headings-font-weight);
      @include font-size(20, rem-v(27));
    }
  }

  &__text {
    margin-bottom: spacer();
  }

  &__link {
    @include font-size(16, rem-v(54));

    display: block;

    &::before {
      @include font-size(54, rem-v(54));

      vertical-align: middle;
      margin-right: 20px;
    }
  }

  &__close {
    cursor: pointer;
    color: $white;
    width: 54px;
    height: 54px;
    display: block;
    border-radius: 27px;
    border: 1px solid $white;
    text-align: center;
    line-height: 54px;
    transition: transform 0.5s ease, top 0.5s ease;
    position: absolute;
    top: calc(100% - 54px);
    opacity: 0;
    z-index: -1;
    transform: rotate(45deg);

    &:hover {
      color: $supernova;
      border-color: $supernova;
    }
  }
}

@include media-breakpoint-up(lg) {
  .article-bg-video {
    &__title {
      h2 {
        @include font-size(36, rem-v(54));
      }
    }
  }
}
</style>
